<template>
  <v-container fluid data-testid="defects-table">
    <v-row>
      <v-col cols="4">
        <div class="mb-6">
          <span class="mr-2 subtitle-1">
            {{$t('logyardConfiguration')}}
            <Icon
              icon="mdi-ruler"
              dataTestId="diameter-weight-view-button"
              :small="false"
              :tooltipText="$t('viewDiameterWeightTable')"
              @icon-clicked="openOrUpdateDialog({ id: dialogId, width: '800px', allowFullscreen: false })"
            />
          </span>
        </div>
        <v-row>
          <v-col cols="auto">
            <v-text-field
              :label="$t('defectCollectionMode')"
              color="primary"
              :value="defectTypeFromInt"
              outlined
              readonly
            ></v-text-field>
          </v-col>
          <v-col cols="auto">
            <v-text-field
              :label="$t('collectLogCounts')"
              color="primary"
              outlined
              :value="location.collectLogCounts ? $t('yes') : $t('no')"
              readonly
            >
            </v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="auto" v-if="location.defectCollectionMode === 2 || location.collectLogCounts">
            <v-text-field
              :label="$t('segmentLength')"
              color="primary"
              :value="location.segmentLength"
              outlined
              readonly
            ></v-text-field>
          </v-col>
          <v-col cols="auto">
            <v-text-field
              :label="$t('ntepCompliant')"
              color="primary"
              :value="location.ntepCompliant ? $t('yes') : $t('no')"
              outlined
              readonly
            />
          </v-col>
        </v-row>
      </v-col>
      <v-col cols="4">
        <v-card class="pb-3">
          <TransporterConfig
            :isEditing="false"
            :transporterByproductTickets="transporterByproductTickets"
            :location="location"
            @updateTransporterByproductTickets="location.transporterByproductTickets = $event"/>
        </v-card>
      </v-col>
    </v-row>
    <v-row v-if="location.collectLogCounts">
      <v-col cols="12">
        <span class="mr-2 subtitle-1">
          {{$t('logLengths')}}
        </span>
      </v-col>
    </v-row>
    <v-row v-if="location.collectLogCounts">
      <v-col cols="12">
        <v-chip v-for="(l, index) in sortedLogLengths"
          :key="index"
          color="black"
          class="white--text ml-2 mb-6"
          readonly>
          <span>{{ `${(l.length).toFixed(2)}'` }}</span>
        </v-chip>
      </v-col>
    </v-row>
    <v-row dense>
      <span class="subtitle-1 font-weight-bold error--text" v-if="showNoDiameterWarning">
        {{$t('noDiameterWeightsAddedForLocation')}}
      </span>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-card outlined>
          <v-card-title>
            <v-row dense>
              <v-col cols="auto">
                <span class="subtitle-1">
                  {{ $t('yardInstructions') }}
                </span>
              </v-col>
              <v-col cols="auto">
                <Icon
                iconColor="primary"
                icon="mdi-plus"
                :tooltipText="$t('addYardInstruction')"
                :small="false"
                @icon-clicked="addYardInstruction"
                />
              </v-col>
            </v-row>
          </v-card-title>
          <v-card-text>
            <DataTable
              dense
              :headers="yardInstructionHeaders()"
              :items="location.yardInstructions"
              :custom-cells="yardInstructionCustomCells()"
              hide-default-footer
              :items-per-page="-1"
            >
              <template #status="{ item }">
                <Icon
                  :icon="statusIcon(item).icon"
                  :iconColor="statusIcon(item).color"
                  :tooltipColor="statusIcon(item).color"
                  :tooltipText="statusIcon(item).tooltip"
                  :small="false"
                />
              </template>
              <template #name="{ item }">
                <Icon
                  v-if="item.isDefault"
                  icon="mdi-star"
                  iconColor="rgba(0,0,0,.87)"
                  :tooltipText="$t('defaultYardInstruction', { location: location.name })"
                />
                <span>{{ item.name }}</span>
              </template>
              <template #actions="{ item }">
                <Icon
                  icon="mdi-pencil"
                  iconColor="success"
                  :tooltipText="$t('edit')"
                  @icon-clicked="openForm(item)"
                />
                <Icon
                  icon="mdi-delete"
                  iconColor="error"
                  :tooltipText="$t('delete')"
                  @icon-clicked="deleteInstruction(item)"
                />
              </template>
            </DataTable>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
   <Dialog :stateId="dialogId" maxWidth="800px">
      <DiameterWeightDialog :location="location" @diameter-weights="setDiameterWeights" @close="refresh"/>
    </Dialog>
    <Dialog :stateId="yardInstructionDialogId" maxWidth="800px">
      <YardInstructionDialog
        v-if="yardInstructionFormOpen"
        :location="location"
        :propInstruction="focusedInstruction"
        @instruction-saved="onInstructionSaved"
        @close="closeYardInstructionDialog"/>
      <ConfirmDelete
        v-if="deleteFormOpen"
        :title="$t('yardInstruction')"
        maxWidth="800px"
        @delete="deleteInstructionConfirmed"
        @cancel-delete="closeYardInstructionDialog"/>
    </Dialog>
  </v-container>
</template>

<script>
import { DefectCollectionType, TransporterTicketCategory, YardInstructionStatus } from '@/utils/Enumerations.js'
import { mapActions } from 'vuex'
import { yardInstructionHeaders, yardInstructionCustomCells } from '@/headers/YardInstructions.js'
export default {
  name: 'DefectConfiguration',

  props: {
    location: {
      type: Object,
      required: true
    }
  },

  components: {
    Dialog: () => import('@/components/Dialog.vue'),
    DiameterWeightDialog: () => import('@/components/settings-components/company-config/location/DiameterWeightDialog.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    TransporterConfig: () => import('@/components/settings-components/company-config/location/TransporterConfig.vue'),
    YardInstructionDialog: () => import('@/components/settings-components/company-config/location/YardInstructionDialog.vue'),
    DataTable: () => import('@/components/core/table/DataTable.vue'),
    ConfirmDelete: () => import('@/components/helper/ConfirmDelete.vue')
  },

  data: () => ({
    dialogId: `defect-configuration-${Math.floor(Math.random() * 65536).toString(16).padStart(4, '0')}`,
    yardInstructionDialogId: `yard-instruction-${Math.floor(Math.random() * 65536).toString(16).padStart(4, '0')}`,
    diameterWeightTableDialog: false,
    diameterWeights: [],
    transporterByproductTickets: false,
    focusedInstruction: undefined,
    yardInstructionFormOpen: false,
    deleteFormOpen: false
  }),

  computed: {
    defectTypeFromInt () { return DefectCollectionType.fromInt(this.location.defectCollectionMode) },

    showNoDiameterWarning () {
      return this.location.defectCollectionMode === 2 && this.diameterWeights.length === 0
    },

    sortedLogLengths () {
      const logLengths = this.location.logLengths
      return logLengths.sort((a, b) => a.length - b.length)
    },

    transporterTicketCategoryIsByproduct () {
      return this.location.transporterTicketCategory === TransporterTicketCategory.Byproducts.value
    }
  },
  created () {
    if (this.location) {
      this.transporterByproductTickets = this.location.transporterTicketCategory === TransporterTicketCategory.Byproducts.value
    }
  },
  watch: {
    diameterWeightTableDialog (isOpen) {
      if (!isOpen) {
        this.diameterWeightTableDialog = false
        this.refresh()
      }
    }
  },

  mounted () {
    this.refresh()
  },

  methods: {
    ...mapActions('defects', ['fetchDiameterInfoForLocation']),
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),
    ...mapActions('yard-instructions', ['deleteYardInstruction']),
    yardInstructionHeaders,
    yardInstructionCustomCells,
    async refresh () {
      this.diameterWeights = await this.fetchDiameterInfoForLocation(this.location.locationId)
    },
    setDiameterWeights (weights) {
      this.diameterWeights = weights
    },

    addYardInstruction () {
      this.focusedInstruction = undefined
      this.yardInstructionFormOpen = true
      this.openOrUpdateDialog({ id: this.yardInstructionDialogId })
    },

    onInstructionSaved () {
      this.closeDialogsAtOrAbove(this.yardInstructionDialogId)
      this.yardInstructionFormOpen = false
      this.deleteFormOpen = false
      this.focusedInstruction = undefined
      this.$emit('instructions-mutated')
    },

    closeYardInstructionDialog () {
      this.closeDialogsAtOrAbove(this.yardInstructionDialogId)
      this.yardInstructionFormOpen = false
      this.deleteFormOpen = false
      this.focusedInstruction = undefined
    },

    statusIcon (item) {
      if (item.status === YardInstructionStatus.Active.value) {
        return {
          icon: 'mdi-checkbox-marked-circle',
          color: 'success',
          tooltip: this.$t('active')
        }
      } else {
        return {
          icon: 'mdi-pause-circle',
          color: 'black',
          tooltip: this.$t('paused')
        }
      }
    },

    openForm (instruction) {
      this.focusedInstruction = instruction
      this.yardInstructionFormOpen = true
      this.openOrUpdateDialog({ id: this.yardInstructionDialogId })
    },

    deleteInstruction (instruction) {
      this.focusedInstruction = instruction
      this.deleteFormOpen = true
      this.openOrUpdateDialog({ id: this.yardInstructionDialogId })
    },

    async deleteInstructionConfirmed () {
      await this.deleteYardInstruction({
        yardInstructionId: this.focusedInstruction.yardInstructionId,
        locationId: this.location.locationId
      })
      this.onInstructionSaved()
    }
  }
}
</script>
